import { Suspense, useEffect, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router"
import { Plus } from "@phosphor-icons/react"
import { t } from "i18next"
import { useUser } from "@/apis/bff"
import { DrawerWrapper } from "@/components/drawer-wrapper"
import { LoadingSpinner } from "@/components/loading-spinner"
import { PreviewDocumentDialogWrapper } from "@/components/preview-document-dialog"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLocationBlocker } from "@/hooks/useLocationBlocker"
import { useNavigationBlocker } from "@/hooks/useNavigationBlocker"
import { cn } from "@/lib/utils"
import { AgendaContainer } from "@/modules/patients/agenda/agenda-container"
import { AlternativeTriageDrawer } from "@/modules/patients/agenda/alternative-triage/alternative-triage-drawer"
import { ExternalAssistanceDrawer } from "@/modules/patients/agenda/external-assistance/external-assistance-drawer"
import { ExternalAssistanceInformationDialog } from "@/modules/patients/agenda/external-assistance-information-dialog"
import { AgendaAppointmentDrawer } from "@/modules/patients/agenda/teleconsultation/agenda-appointment-drawer/agenda-appointment-drawer"
import {
  OPEN_TELECONSULTATION_PARAM,
  OPEN_TELECONSULTATION_VALUE,
  TeleconsultationDrawer,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import {
  OPEN_TIME_WINDOW_PARAM,
  OPEN_TIME_WINDOW_VALUE,
  TimeWindowDrawer,
} from "@/modules/patients/agenda/time-window/time-window-drawer"
import {
  TimeWindowFormSchema,
  TimeWindowNavigationState,
} from "@/modules/patients/agenda/time-window/time-window-schema"
import { AGENDA_TYPE, TypesFilter } from "@/modules/patients/agenda/types-filter"
import { PreventionProgramsContent } from "@/modules/patients/detail/health-information/condition/prevention-programs"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePreviewParam } from "@/modules/patients/hooks/usePreviewParam"
import { AppointmentAgenda } from "@/modules/patients/libs/categorize-date-ranges"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { usePatientPrevention } from "@/modules/patients/services/usePatientPrevention"
import { default as useCallContextStore } from "@/stores/useCallContextStore"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { getIsDirty, useDrawerStore } from "@/stores/useDrawerStore"

export type AgendaEntryStatus = "PLANNED" | "ONGOING" | "PAST"

export type AgendaAction = "CREATE" | "EDIT" | "COPY" | "VIEW"

export const PatientAgenda = () => {
  const { patient, isEditMode } = useOutletContext<{ patient: PatientFragment$data; isEditMode: boolean }>()
  const { role } = useUser()
  const temporaryNotes = useCallContextStore((state) => state.temporaryNotes)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const countryCode = useCountryParam()
  const isPreview = usePreviewParam()
  const { preventionProgramItems } = usePatientPrevention(patient.id, countryCode)
  const navigate = useNavigate()
  const location = useLocation()

  const [types, setTypes] = useState<AGENDA_TYPE[]>([])
  const [selectedAgendaRowId, setSelectedAgendaRowId] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const [isOpenExternalAssistanceDialog, setIsOpenExternalAssistanceDialog] = useState<boolean>(
    searchParams.has("fromExternalAssistance"),
  )
  const hasHandledOpenTimeWindowParamRef = useRef(false)
  const hasHandledOpenTeleconsultationParamRef = useRef(false)

  const onCloseExternalAssistanceDialog = () => {
    const params = new URLSearchParams(searchParams)
    params.delete("fromExternalAssistance")
    navigate({ search: params.toString() }, { replace: true })
    setIsOpenExternalAssistanceDialog(false)
  }

  const onRowClick = (agenda: AppointmentAgenda, mode: AgendaAction) => {
    if (agenda.__typename === "ch_AlternativeTriage" || agenda.__typename === "de_AlternativeTriage") {
      setSelectedAgendaRowId(agenda.id)
      openDrawer(
        () => (
          <Suspense>
            <AlternativeTriageDrawer selectedAlternativeTriageId={agenda.id} />
          </Suspense>
        ),
        "AlternativeTriageDrawer",
      )
      return
    }
    if (agenda.__typename === "ch_ExternalAssistance") {
      openDrawer(
        () => (
          <Suspense>
            <ExternalAssistanceDrawer selectedExternalAssistanceId={agenda.id} />
          </Suspense>
        ),
        "ExternalAssistanceDrawer",
      )
      return
    }
    if (!agenda.appointment) {
      setSelectedAgendaRowId(agenda.id)
      const referralToCareProviderId = agenda.referralToCareProvider?.id
      const timeWindow: TimeWindowFormSchema = {
        id: mode === "EDIT" || !isEditMode ? agenda.id : undefined,
        referredBy: agenda.referredBy ?? "SELF",
        referralToCareProviderId: referralToCareProviderId ?? "",
        nameOfDoctor: agenda.referralToDoctorName ?? "",
        referralReason: agenda.referralReason === "REFERRAL" ? "APPOINTMENT_REGISTRATION" : agenda.referralReason!,
        dateFrom: new Date(agenda.dateFrom!),
        dateTo: new Date(agenda.dateTo!),
        internalComment: agenda.internalComment ?? "",
        referredByExternalCareProviderId: agenda.referredByExternalCareProviderId! ?? "",
        updatedAt: agenda.updatedAt ? new Date(agenda.updatedAt) : undefined,
        updatedBy: agenda.updatedBy ?? "",
        createdAt: agenda.createdAt ? new Date(agenda.createdAt!) : undefined,
        createdBy: agenda.createdBy ?? "",
        correction: agenda.errorCorrection ? "timeWindowErrorCorrection" : "timeWindow",
        referenceDate: agenda.referenceDate ? new Date(agenda.referenceDate) : new Date(),
        alternativeTriage: agenda?.alternativeTriage ?? false,
        alternativeTriagePhoneNumber: agenda?.alternativeTriageEntry?.phoneNumber ?? "",
        alternativeTriageReason: agenda?.alternativeTriageEntry?.reason ?? "",
        involvesPreventionProgram: !!agenda.involvesPreventionProgram,
      }
      const progressNoteId = agenda?.progressNoteId ?? null

      const isErrorCorrectionReadOnly = agenda.errorCorrection && role !== "CustomerServiceCenter"

      if (isEditMode && !isErrorCorrectionReadOnly) {
        openDrawer(
          () => (
            <TimeWindowDrawer
              setSelectedAgendaRowId={setSelectedAgendaRowId}
              mode={isErrorCorrectionReadOnly ? "VIEW" : mode}
              timeWindow={timeWindow}
              progressNoteId={progressNoteId}
            />
          ),
          "TimeWindowDrawer",
        )
      } else {
        openDrawer(
          () => (
            <TimeWindowDrawer
              setSelectedAgendaRowId={setSelectedAgendaRowId}
              mode="VIEW"
              timeWindow={timeWindow}
              progressNoteId={progressNoteId}
            />
          ),
          "TimeWindowDrawer",
        )
      }
    } else if (agenda.appointment) {
      if (isEditMode) {
        setSelectedAgendaRowId(agenda.id)
      }
      if (!isEditMode || agenda.appointment.status === "CANCELLED" || agenda.appointment.status === "CLOSED") {
        openDrawer(
          () => <AgendaAppointmentDrawer selectedAppointmentId={agenda.appointment!.id} />,
          "AgendaAppointmentDrawer",
        )
      } else {
        openDrawer(
          () => (
            <TeleconsultationDrawer
              mode={{ kind: "EDITING_APPOINTMENT", data: { appointment: agenda.appointment! } }}
              onClose={() => setSelectedAgendaRowId(null)}
              countryCode={countryCode}
              patientId={patient.id}
              appointmentId={agenda.appointment?.id ?? null}
            />
          ),
          "TeleconsultationDrawer",
        )
      }
    }
  }

  const onNewTimeWindowClick = () => {
    openDrawer(() => <TimeWindowDrawer mode="CREATE" />, "TimeWindowDrawer")
  }

  const onTeleconsultationClick = () => {
    openDrawer(
      () => (
        <TeleconsultationDrawer
          mode={{ kind: "ADD_APPOINTMENT", data: {} }}
          countryCode={countryCode}
          patientId={patient.id}
          appointmentId={null}
        />
      ),
      "TeleconsultationDrawer",
    )
  }

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  useNavigationBlocker(getIsDirty)

  const blocker = useLocationBlocker()

  useEffect(() => {
    if (hasHandledOpenTimeWindowParamRef.current) return
    const openTimeWindow = searchParams.get(OPEN_TIME_WINDOW_PARAM)

    if (!openTimeWindow) return
    hasHandledOpenTimeWindowParamRef.current = true

    if (openTimeWindow === OPEN_TIME_WINDOW_VALUE.CREATE) {
      openDrawer(() => <TimeWindowDrawer mode="CREATE" />, "TimeWindowDrawer")
    } else if (openTimeWindow === OPEN_TIME_WINDOW_VALUE.ALTERNATIVE_TRIAGE) {
      const navigationState: TimeWindowNavigationState = location.state
      const alternativeTriageData = navigationState?.alternativeTriage
      openDrawer(
        () => (
          <TimeWindowDrawer mode="CREATE" defaultReferredBy="MEDGATE" alternativeTriageData={alternativeTriageData} />
        ),
        "TimeWindowDrawer",
      )
    }

    const params = new URLSearchParams(searchParams)
    params.delete(OPEN_TIME_WINDOW_PARAM)
    navigate({ search: params.toString() }, { replace: true })
  }, [searchParams, navigate, openDrawer, location.state])

  useEffect(() => {
    if (hasHandledOpenTeleconsultationParamRef.current) return
    const openTeleconsultation = searchParams.get(OPEN_TELECONSULTATION_PARAM)

    if (!openTeleconsultation) return
    hasHandledOpenTeleconsultationParamRef.current = true

    if (openTeleconsultation === OPEN_TELECONSULTATION_VALUE.ADD_APPOINTMENT) {
      openDrawer(
        () => (
          <TeleconsultationDrawer
            mode={{ kind: "ADD_APPOINTMENT", data: { defaultReason: temporaryNotes } }}
            countryCode={countryCode}
            patientId={patient.id}
            appointmentId={null}
          />
        ),
        "TeleconsultationDrawer",
      )
    }

    const params = new URLSearchParams(searchParams)
    params.delete(OPEN_TELECONSULTATION_PARAM)
    navigate({ search: params.toString() }, { replace: true })
  }, [searchParams, navigate, openDrawer, countryCode, patient.id, temporaryNotes])

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (useDrawerStore.getState().DrawerContent && useDrawerStore.getState().isDirty) {
        confirm({
          onConfirm: () => {
            if (useDrawerStore.getState().onDrawerClose) {
              useDrawerStore.getState().onDrawerClose?.()
            } else {
              useDrawerStore.getState().closeDrawer()
            }
            useDrawerStore.getState().setDirty(false)
            blocker.proceed()
            cancel()
          },
        })
      } else {
        useDrawerStore.getState().closeDrawer()
        blocker.proceed()
      }
    }
  }, [blocker, cancel, confirm])

  if (!patient) return null

  return (
    <>
      <div className={cn("flex", isPreview ? "h-screen" : "h-full")}>
        <div className="grid grow grid-cols-[minmax(0,1fr)_auto] grid-rows-1">
          <main className="flex flex-col">
            <div
              data-testid="patient-agenda"
              className="border-opacity-10 text-on-surface bg-surface inline-flex h-16 w-full items-center justify-between border-b p-3 px-8 text-xl font-bold">
              {t("patient.agenda.patientAgenda")}
              <div className="flex gap-2">
                {isEditMode && countryCode === "CH" && (
                  <div className="flex items-center justify-end gap-4">
                    <Button
                      onClick={onNewTimeWindowClick}
                      type="button"
                      variant="secondary"
                      data-testid="patient-time-window-btn">
                      <Plus className="text-on-surface-subdued" />
                      {t("patient.agenda.timeWindow")}
                    </Button>
                  </div>
                )}
                {isEditMode && (
                  <div className="flex items-center justify-end gap-4">
                    <Button
                      onClick={onTeleconsultationClick}
                      type="button"
                      variant="primary"
                      data-testid="patient-teleconsultation-btn">
                      <Plus />
                      {t("patient.agenda.teleconsultation")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="p-8">
              <TypesFilter countryCode={countryCode} value={types} onChange={setTypes} />
            </div>
            <ScrollArea viewportClassName="max-h-none" className="h-full">
              {isEditMode && (
                <div className="flex w-full flex-col gap-2">
                  <PreventionProgramsContent
                    isOpenConditionDrawer={false}
                    patientPreventionItems={preventionProgramItems}
                    isAgendaPage={true}
                    closeDrawer={closeDrawer}
                  />
                </div>
              )}
              <ErrorBoundary
                fallback={
                  <div className="flex justify-center gap-4 pt-0.5 align-middle">
                    <p className="flex items-center">{t("common.somethingWentWrong")}</p>
                    <Button variant="secondary" onClick={() => window.location.reload()}>
                      {t("common.button.refresh")}
                    </Button>
                  </div>
                }>
                <Suspense fallback={<LoadingSpinner isFullPage={true} size={32} className="mt-[20%]" />}>
                  <AgendaContainer
                    onRowClick={onRowClick}
                    patient={patient}
                    selectedAgendaRowId={selectedAgendaRowId}
                    setSelectedAgendaRowId={setSelectedAgendaRowId}
                    types={types}
                  />
                </Suspense>
              </ErrorBoundary>
            </ScrollArea>
          </main>
          <DrawerWrapper containerId="agenda-drawer" />
          <PreviewDocumentDialogWrapper />
        </div>
      </div>

      {isOpenExternalAssistanceDialog && (
        <Suspense>
          <ExternalAssistanceInformationDialog patientId={patient.id} onClose={onCloseExternalAssistanceDialog} />
        </Suspense>
      )}
    </>
  )
}
